/**
 * Consumer Journeys — top-level nav page.
 *
 * The banker-push demo flow lives here so any preview admin can:
 *   1. Simulate a banker pushing product+documents to Altara
 *   2. Copy or open the generated journey URL (what the banker would email)
 *   3. See a live push history of every journey created via inbound push
 *
 * The consumer-facing journey itself renders at /j/{token} (public route,
 * no auth). API-key management stays in Administration → Bankers.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Route, Send, KeyRound, Info, FileText, User as UserIcon, ArrowRight,
} from "lucide-react";
import { SimulatePushSection, PushHistorySection } from "@/components/banker/BankerJourneySections";

export default function ConsumerJourneysPage() {
  const [history, setHistory] = useState([]);

  const refresh = () => {
    api.get("/admin/banker/push-history").then(r => setHistory(r.data)).catch(() => setHistory([]));
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="journeys-page" className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Consumer Journeys</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Banker push → Altara → Consumer</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            When a banker prepares a quote or proposal in their core system, they push it to Altara together with
            every disclosure and supporting document. Altara mints a signed link and returns it — the banker then
            emails that link to the consumer. This page lets you simulate and inspect that flow.
          </p>
        </div>
        <Link to="/administration" className="text-xs text-altara-teal hover:text-altara-deep inline-flex items-center gap-1">
          <KeyRound className="w-3.5 h-3.5" /> Manage banker API keys
        </Link>
      </header>

      {/* How the flow works */}
      <FlowExplainer />

      {/* Simulate push */}
      <SimulatePushSection onCreated={refresh} />

      {/* Push history */}
      <PushHistorySection history={history} onRefresh={refresh} />
    </div>
  );
}

function FlowExplainer() {
  const steps = [
    { icon: FileText, label: "Banker prepares", body: "Quote · disclosures · brochure · policy wording · statement of advice — assembled in the banker's core system." },
    { icon: Send,     label: "Push to Altara",   body: "POST /api/banker/journeys/create with consumer + product + documents + callback URL." },
    { icon: Route,    label: "Altara mints link", body: "Signed HMAC token, 30-day expiry. Documents become hash-chained evidence." },
    { icon: UserIcon, label: "Consumer opens",   body: "Banker emails the link. Consumer reads, asks NAVI, decides, acknowledges." },
  ];
  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">
        <Info className="w-3.5 h-3.5" /> How the flow works
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-4">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="relative border border-slate-100 rounded-sm p-4 bg-slate-50/40">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-sm bg-altara-deep text-white text-[11px] font-mono font-semibold flex items-center justify-center">{i + 1}</span>
                <Icon className="w-4 h-4 text-altara-teal" />
              </div>
              <div className="mt-2 text-sm font-semibold text-altara-ink">{s.label}</div>
              <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">{s.body}</div>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 bg-white" />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
